import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { type ColumnDef, DataTable } from '@/components/common/data-table';
import type { ProcedureRow } from '@/services/animal-management';

export const PROCEDURE_HISTORY_STORAGE_KEY =
  'animal-management-procedure-history';

const formatNumber = (value: number | null) =>
  value == null ? '' : value.toLocaleString();

type ProcedureHistoryTableProps = {
  procedures: ProcedureRow[];
};

/**
 * Procedure history for the animal detail modal (WI 38993). Columns can be
 * dragged into a new order, but the order only sticks once the user saves
 * the view.
 */
export function ProcedureHistoryTable({
  procedures,
}: ProcedureHistoryTableProps) {
  const { t } = useTranslation();

  const columns = useMemo<ColumnDef<ProcedureRow>[]>(() => {
    const col = (key: string, fallback: string) =>
      t(`animalManagement.columns.${key}`, fallback);
    return [
      {
        key: 'procedureDate',
        header: t('common.date', 'Date'),
        width: 90,
        render: (row) => row.procedureDate.slice(0, 10),
      },
      {
        key: 'procedureName',
        header: col('procedureName', 'Procedure Name'),
        width: 160,
        render: (row) => row.procedureName,
      },
      {
        key: 'procedureCode',
        header: col('procedureCode', 'Procedure Code'),
        width: 100,
        render: (row) => row.procedureCode,
      },
      {
        key: 'description',
        header: col('procedureDescription', 'Procedure Description'),
        width: 200,
        render: (row) => <span title={row.description}>{row.description}</span>,
      },
      {
        key: 'ownerName',
        header: col('owner', 'Owner'),
        width: 140,
        render: (row) => row.ownerName,
      },
      {
        key: 'weight',
        header: col('weight', 'Weight'),
        width: 80,
        align: 'right',
        render: (row) => formatNumber(row.weight),
      },
      {
        key: 'hipHeight',
        header: col('hipHeight', 'Hip Height'),
        width: 80,
        align: 'right',
        render: (row) => formatNumber(row.hipHeight),
      },
      {
        key: 'snapshotWeight',
        header: col('snapshotWeight', 'Snapshot Weight'),
        width: 110,
        align: 'right',
        render: (row) => formatNumber(row.snapshotWeight),
      },
      {
        key: 'pcPregnant',
        header: col('pcPregnant', 'PC - Pregnant?'),
        width: 100,
        render: (row) =>
          row.pcPregnant == null
            ? ''
            : row.pcPregnant
              ? t('common.yes', 'Yes')
              : t('common.no', 'No'),
      },
      {
        key: 'breedingName',
        header: col('breeding', 'Breeding'),
        width: 220,
        render: (row) => (
          <span title={row.breedingName}>{row.breedingName}</span>
        ),
      },
    ];
  }, [t]);

  return (
    <DataTable
      rows={procedures}
      columns={columns}
      getRowId={(row) => row.id}
      storageKey={PROCEDURE_HISTORY_STORAGE_KEY}
      layoutPersistence="manual"
      reorderable
      fillWidth
      emptyMessage={t(
        'animalManagement.editAnimal.noProcedures',
        'No procedures recorded',
      )}
    />
  );
}
